import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api.js';

const STATUS_BADGE = {
  open: 'bg-blue-100 text-blue-700 border-blue-300',
  claimed: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  resolved: 'bg-green-100 text-green-700 border-green-300',
  closed: 'bg-grey-100 text-grey-700 border-grey-300',
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState('');
  const [rewritten, setRewritten] = useState(null);
  const [rewriting, setRewriting] = useState(false);
  const [sending, setSending] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [closing, setClosing] = useState(false);

  function load() {
    api.get(`/tickets/${id}`).then(({ data }) => setData(data));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function claim() {
    setClaiming(true);
    try {
      await api.post(`/tickets/${id}/claim`);
      load();
    } finally {
      setClaiming(false);
    }
  }

  async function requestRewrite() {
    if (!draft.trim()) return;
    setRewriting(true);
    try {
      const { data: res } = await api.post(`/tickets/${id}/rewrite`, { draft });
      setRewritten(res.rewritten);
    } finally {
      setRewriting(false);
    }
  }

  // The rewrite is only ever a suggestion. Sending is always a separate,
  // explicit human action, whether or not a rewrite was used.
  async function send(body, wasRewritten) {
    setSending(true);
    try {
      await api.post(`/tickets/${id}/reply`, {
        body,
        gptRewritten: wasRewritten,
        originalDraft: wasRewritten ? draft : undefined,
      });
      setDraft('');
      setRewritten(null);
      load();
    } finally {
      setSending(false);
    }
  }

  async function resolve() {
    if (!confirm('Mark this ticket as resolved?')) return;
    setResolving(true);
    try {
      await api.patch(`/tickets/${id}/status`, { status: 'resolved' });
      load();
    } finally {
      setResolving(false);
    }
  }

  async function close() {
    if (!confirm('Close this ticket?')) return;
    setClosing(true);
    try {
      await api.patch(`/tickets/${id}/status`, { status: 'closed' });
      load();
    } finally {
      setClosing(false);
    }
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-grey-900"></div>
        <p className="text-grey-500 font-medium mt-2 ml-3">Loading ticket...</p>
      </div>
    );
  }

  const { ticket, messages, customerHistory } = data;
  const canReply = ticket.status !== 'closed' && ticket.status !== 'resolved';

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <button
          onClick={() => navigate('/')}
          className="text-grey-600 hover:text-grey-900 flex items-center font-medium tracking-wide transition"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>

        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold text-grey-900 tracking-tight">{ticket.subject}</h1>
            <p className="text-grey-600 font-medium tracking-wide">Ticket #{id.slice(0, 8)}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize border ${STATUS_BADGE[ticket.status]}`}>
              {ticket.status}
            </span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 border border-blue-300">
              {ticket.products.name}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white border border-pampa-200 rounded-lg overflow-hidden flex flex-col h-[600px]">
            <div className="px-6 py-4 border-b border-pampa-200 bg-pampa-50 flex-shrink-0">
              <h2 className="text-lg font-semibold text-grey-900 tracking-wide">Messages</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-grey-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <p className="text-grey-400 font-medium">No messages yet</p>
                </div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === 'customer' ? 'justify-start' : 'justify-end'}`}>
                    <div
                      className={`max-w-xl rounded-lg p-4 ${
                        m.sender_type === 'customer' ? 'bg-pampa-50 text-grey-900 border border-pampa-200' : 'bg-grey-900 text-white'
                      }`}
                    >
                      <p className="text-sm font-medium whitespace-pre-wrap tracking-wide">{m.body}</p>
                      {m.gpt_rewritten && (
                        <span className="inline-block mt-2 px-2 py-1 bg-purple-100 text-purple-700 border border-purple-300 text-xs font-medium rounded">
                          AI Enhanced
                        </span>
                      )}
                      <p className="text-xs opacity-70 mt-2 font-medium">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {canReply && (
              <div className="px-6 py-4 border-t border-pampa-200 bg-pampa-50 flex-shrink-0">
                <div className="space-y-4">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type your reply..."
                    rows={4}
                    className="w-full px-4 py-3 bg-white border border-pampa-200 rounded-lg text-grey-900 placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-grey-900 focus:border-transparent font-medium"
                  />

                  {rewritten && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-purple-900 tracking-wide">AI Enhanced Version:</p>
                        <button type="button" onClick={() => setRewritten(null)} className="text-purple-600 hover:text-purple-800 transition">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <p className="text-sm text-purple-900 font-medium whitespace-pre-wrap mb-3 tracking-wide">{rewritten}</p>
                      <button
                        type="button"
                        onClick={() => send(rewritten, true)}
                        disabled={sending}
                        className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition tracking-wide disabled:opacity-50"
                      >
                        Send This Version
                      </button>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => send(draft, false)}
                      disabled={!draft.trim() || sending}
                      className="px-6 py-2 bg-grey-900 text-white rounded-lg hover:bg-grey-800 transition disabled:opacity-50 font-medium tracking-wide"
                    >
                      {sending ? 'Sending...' : 'Send Reply'}
                    </button>
                    <button
                      type="button"
                      onClick={requestRewrite}
                      disabled={!draft.trim() || rewriting}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 font-medium tracking-wide"
                    >
                      {rewriting ? 'Rewriting...' : '✨ AI Enhance'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-pampa-200 rounded-lg p-6">
            <h3 className="font-medium text-grey-900 mb-4 tracking-wide">Actions</h3>
            <div className="space-y-3">
              {ticket.status === 'open' && (
                <button
                  onClick={claim}
                  disabled={claiming}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-medium tracking-wide"
                >
                  {claiming ? 'Claiming...' : 'Claim Ticket'}
                </button>
              )}

              {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                <button
                  onClick={resolve}
                  disabled={resolving}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 font-medium tracking-wide"
                >
                  {resolving ? 'Resolving...' : 'Mark Resolved'}
                </button>
              )}

              {ticket.status !== 'closed' && (
                <button
                  onClick={close}
                  disabled={closing}
                  className="w-full px-4 py-2 bg-grey-600 text-white rounded-lg hover:bg-grey-700 transition disabled:opacity-50 font-medium tracking-wide"
                >
                  {closing ? 'Closing...' : 'Mark as Closed'}
                </button>
              )}
            </div>
          </div>

          <div className="bg-white border border-pampa-200 rounded-lg p-6">
            <h3 className="font-medium text-grey-900 mb-4 tracking-wide">Customer Info</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-grey-500 font-medium uppercase tracking-wider mb-1">Name</p>
                <p className="text-sm text-grey-900 font-medium tracking-wide">{ticket.customers?.full_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-grey-500 font-medium uppercase tracking-wider mb-1">Email</p>
                <p className="text-sm text-grey-900 font-medium tracking-wide">{ticket.customers?.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-grey-500 font-medium uppercase tracking-wider mb-1">Product</p>
                <p className="text-sm text-grey-900 font-medium tracking-wide">{ticket.products?.name}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-pampa-200 rounded-lg p-6">
            <h3 className="font-medium text-grey-900 mb-4 tracking-wide">Customer History</h3>
            {customerHistory.length === 0 ? (
              <p className="text-sm text-grey-400 font-medium">No other tickets across any product.</p>
            ) : (
              <div className="space-y-2">
                {customerHistory.map((h) => (
                  <Link
                    key={h.id}
                    to={`/tickets/${h.id}`}
                    className="block rounded-lg border border-pampa-200 p-3 hover:border-grey-400 transition"
                  >
                    <p className="text-sm text-grey-900 font-medium truncate">{h.subject}</p>
                    <p className="text-xs text-grey-500 font-medium">
                      {h.products?.name} · {h.status}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
